(() => {
  'use strict';

  const MOBILE_NAV_QUERY = '(max-width: 1380px)';
  const selectedProjects = new Map();

  function escapeHTML(value) {
    return String(value ?? '').replace(/[&<>'"]/g, character => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
    })[character]);
  }

  function cleanCatalogText(value) {
    return String(value ?? '')
      .replace(new RegExp(String.fromCodePoint(0x1f50d), 'gu'), '')
      .replace(new RegExp(`${String.fromCharCode(0xfffd)}+`, 'g'), ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function asArray(value) {
    if (Array.isArray(value)) return value.filter(item => item !== null && item !== undefined && item !== '');
    if (value === null || value === undefined || value === '') return [];
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) return parsed.filter(Boolean);
        } catch { /* Fall back to comma-separated values. */ }
      }
      return trimmed.split(',').map(item => item.trim()).filter(Boolean);
    }
    return [value].filter(Boolean);
  }

  function normalizeProject(project = {}) {
    const normalized = { ...project };
    normalized.gallery = asArray(project.gallery).map(item => typeof item === 'string' ? item : item?.url || item?.raw_url || item?.thumbnailUrl || '').filter(Boolean);
    normalized.categories = asArray(project.categories || project.display_categories).map(item => typeof item === 'string' ? item : item?.slug || item?.name || '').filter(Boolean);
    normalized.loaders = asArray(project.loaders || project.loader).map(item => typeof item === 'string' ? item : item?.name || '').filter(Boolean);
    normalized.versions = asArray(project.versions || project.game_versions).map(String).filter(Boolean);
    normalized.source_links = asArray(project.source_links).filter(item => item && typeof item === 'object');
    return normalized;
  }

  function formatCount(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return String(value || '');
    if (number >= 1e9) return `${(number / 1e9).toFixed(number >= 1e10 ? 0 : 1)}B`;
    if (number >= 1e6) return `${(number / 1e6).toFixed(number >= 1e7 ? 0 : 1)}M`;
    if (number >= 1e3) return `${(number / 1e3).toFixed(number >= 1e4 ? 0 : 1)}K`;
    return String(Math.round(number));
  }

  function niceType(value) {
    const key = String(value || '').toLowerCase().replace(/[\s_-]+/g, '');
    return ({
      mod: 'Mod', resourcepack: 'Resource Pack', resource: 'Resource Pack', texturepack: 'Resource Pack',
      shader: 'Shader', datapack: 'Data Pack', datapacks: 'Data Pack', modpack: 'Modpack',
      plugin: 'Plugin', world: 'World / Map', map: 'World / Map', skin: 'Skins & Cosmetics',
      cosmetic: 'Skins & Cosmetics', server: 'Server'
    })[key] || value || 'Project';
  }

  function normalizeType(value) {
    const key = String(value || 'project').toLowerCase().replace(/[\s_-]+/g, '');
    if (key.includes('resource') || key.includes('texture')) return 'resourcepack';
    if (key.includes('map')) return 'world';
    if (key.includes('data')) return 'datapack';
    if (key.includes('skin') || key.includes('cosmetic')) return 'skin';
    return key || 'project';
  }

  function sourceKey(project) {
    const combined = [
      project.platform, project.source, project.url, project.source_url, project.external_url,
      ...asArray(project.source_links).flatMap(link => [link?.platform, link?.url])
    ].join(' ').toLowerCase();
    if (combined.includes('curseforge') || project.cf_id || project.cf_slug) return 'curseforge';
    if (combined.includes('modrinth') || project.modrinth_id || project.modrinth_slug) return 'modrinth';
    if (combined.includes('planetminecraft') || combined.includes('planet minecraft')) return 'planetminecraft';
    if (combined.includes('minecraftskins') || normalizeType(project.project_type || project.type) === 'skin') return 'skin';
    if (combined.includes('github')) return 'github';
    if (combined.includes('hangar')) return 'hangar';
    if (combined.includes('spigot')) return 'spigotmc';
    if (normalizeType(project.project_type || project.type) === 'server' || project.address) return 'server';
    if (normalizeType(project.project_type || project.type) === 'world') return 'world';
    return 'official';
  }

  function isPaperMCDownloadListing(project) {
    const combined = [
      project.platform, project.source, project.title, project.name, project.url, project.source_url, project.external_url,
      ...asArray(project.source_links).flatMap(link => [link?.platform, link?.label, link?.url])
    ].join(' ').toLowerCase();
    if (!combined) return Boolean(project.papermc_id);
    if (combined.includes('hangar.papermc.io')) return false;
    return Boolean(project.papermc_id)
      || combined.includes('papermc downloads')
      || combined.includes('paper mc downloads')
      || combined.includes('papermc.io/downloads')
      || combined.includes('papermc.io/software')
      || combined.includes('source-papermc');
  }

  function safeExternalURL(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    try {
      const url = new URL(raw, location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.toString() : '';
    } catch {
      return raw.startsWith('/') ? raw : '';
    }
  }

  function isMisleadingPlaceholder(value) {
    const raw = String(value || '').toLowerCase();
    if (!raw) return false;
    return raw.includes('/assets/img/fmm-logo')
      || raw.includes('/assets/img/fmm-social')
      || raw.includes('/assets/img/fmm-favicon')
      || raw.includes('/assets/img/generated/server-')
      || raw.includes('/assets/img/generated/source-');
  }

  function projectMediaURL(value) {
    const safe = safeExternalURL(value);
    return safe && !isMisleadingPlaceholder(safe) ? safe : '';
  }


  function imageDeliveryURL(value, width = 560, height = 350, fit = 'cover') {
    const original = safeExternalURL(value);
    if (!original) return { src: '', fallback: '' };
    try {
      const url = new URL(original, location.origin);
      const proxyHosts = new Set(['media.forgecdn.net', 'static.planetminecraft.com']);
      if (url.origin === location.origin || !proxyHosts.has(url.hostname)) return { src: original, fallback: '' };
      const proxy = new URL('/api/project-image', location.origin);
      proxy.searchParams.set('url', url.toString());
      proxy.searchParams.set('w', String(width));
      proxy.searchParams.set('h', String(height));
      proxy.searchParams.set('fit', fit);
      return { src: proxy.pathname + proxy.search, fallback: original };
    } catch {
      return { src: original, fallback: '' };
    }
  }

  function imageAttributes(delivery) {
    return delivery.fallback ? ` data-fallback-src="${escapeHTML(delivery.fallback)}"` : '';
  }

  function projectRoute(project, type) {
    if (project.page) return project.page;
    const source = sourceKey(project);
    const identifier = project.cf_id || project.source_id || project.modrinth_slug || project.slug ||
      project.modrinth_id || project.project_id || project.spigot_id || project.id || project.title || 'project';
    const id = encodeURIComponent(String(identifier).replace(/^(?:cf|curseforge|modrinth|mr|spigotmc|spigot)[:_-]/i, ''));
    const encodedType = encodeURIComponent(type);
    if (source === 'skin') return `/project?source=skin&id=${id}`;
    if (source === 'world' || source === 'planetminecraft') return `/project?source=world&id=${id}`;
    if (source === 'server') return `/project?source=server&id=${id}`;
    if (source === 'curseforge') {
      const slug = encodeURIComponent(project.cf_slug || project.slug || project.title || 'project');
      return `/p/curseforge/${id}/${slug}?type=${encodedType}`;
    }
    if (source === 'modrinth') {
      const sourceId = encodeURIComponent(String(project.modrinth_id || project.source_id || identifier));
      const slug = encodeURIComponent(project.modrinth_slug || project.slug || project.title || 'project');
      return `/p/modrinth/${sourceId}/${slug}?type=${encodedType}`;
    }
    if (source === 'github') return `/project?source=github&id=${encodeURIComponent(project.url || identifier)}&type=${encodedType}`;
    if (source === 'hangar') return `/project?source=hangar&id=${encodeURIComponent(project.url || identifier)}`;
    if (source === 'spigotmc') return `/project?source=spigotmc&id=${id}`;
    const sourceURL = safeExternalURL(project.source_url || project.url || project.external_url);
    return `/project?source=official&id=${id}&type=${encodedType}${sourceURL ? `&url=${encodeURIComponent(sourceURL)}` : ''}`;
  }

  function sourceLabel(project) {
    const source = sourceKey(project);
    return ({
      curseforge: 'Official CurseForge link', modrinth: 'Official Modrinth link',
      planetminecraft: 'Official Planet Minecraft link', world: 'Official project source',
      github: 'Official GitHub repository', hangar: 'Official Hangar link',
      spigotmc: 'Official SpigotMC link',
      skin: 'Official skin source', server: 'Official server source', official: 'Official source link'
    })[source] || 'Official source link';
  }

  function projectIcon(type) {
    return ({
      mod: 'MOD', resourcepack: 'PACK', shader: 'SHDR', world: 'MAP',
      skin: 'SKIN', server: 'SERVER', plugin: 'PLUGIN', datapack: 'DATA', modpack: 'PACK'
    })[type] || 'PROJECT';
  }

  function dateLabel(value) {
    const date = Date.parse(value || '');
    if (!date) return '';
    return new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  function loaderLabel(project) {
    const text = [project.loader, ...asArray(project.loaders), ...asArray(project.categories)].join(' ').toLowerCase();
    return [...new Set(text.match(/fabric|forge|neoforge|quilt|paper|spigot|bukkit|iris/g) || [])]
      .slice(0, 4).map(item => item === 'neoforge' ? 'NeoForge' : item[0].toUpperCase() + item.slice(1)).join(', ');
  }

  function versionLabel(project) {
    const versions = asArray(project.versions || project.game_versions);
    return versions.map(value => String(value).trim()).filter(Boolean).slice(0, 4).join(', ');
  }

  function categoryPills(project) {
    return asArray(project.categories || project.display_categories).filter(Boolean).slice(0, 4)
      .map(category => `<span class="pill">${escapeHTML(String(category).replaceAll('-', ' '))}</span>`).join('');
  }

  function cardMedia(project, type) {
    const rawTitle = cleanCatalogText(project.title || project.name || 'Minecraft project') || 'Minecraft project';
    const title = escapeHTML(rawTitle);
    const iconURL = projectMediaURL(project.icon || project.icon_url || project.project_icon || project.avatar_api || '');
    const gallery = asArray(project.gallery)
      .map(item => typeof item === 'string' ? item : item?.url || item?.raw_url || item?.thumbnailUrl || '')
      .map(projectMediaURL)
      .filter(Boolean);
    const screenshotURL = projectMediaURL(project.featured_gallery || project.screenshot || project.banner || project.thumbnail || project.image || gallery[0] || '');
    const fallback = projectIcon(type);

    if (type === 'skin') {
      const rawTexture = safeExternalURL(project.skin_api || project.download_url || screenshotURL || iconURL);
      if (!rawTexture || !project.verified_skin) return '';
      const texture = `/api/skin-texture?url=${encodeURIComponent(rawTexture)}`;
      return `<div class="project-media skin-media" data-preview-required="true"><img class="skin-texture-preview" data-required-preview="true" src="${escapeHTML(texture)}" alt="${title} verified skin preview" width="560" height="350" loading="lazy" decoding="async" referrerpolicy="no-referrer"></div>`;
    }

    const liveServerIcon = type === 'server' && project.address
      ? `https://api.mcsrvstat.us/icon/${encodeURIComponent(project.address)}`
      : '';
    const mainURL = type === 'server' ? (screenshotURL || iconURL || liveServerIcon) : (screenshotURL || iconURL);
    const main = imageDeliveryURL(mainURL, 560, 350, screenshotURL ? 'cover' : 'contain');
    const logo = iconURL && screenshotURL && iconURL !== screenshotURL ? imageDeliveryURL(iconURL, 96, 96, 'contain') : { src: '', fallback: '' };
    const addressIcon = type === 'server' && project.address && mainURL !== liveServerIcon
      ? `<img class="project-logo server-icon-live" src="https://api.mcsrvstat.us/icon/${encodeURIComponent(project.address)}" alt="${title} server icon" width="58" height="58" loading="lazy" decoding="async">`
      : '';
    return `<div class="project-media${!screenshotURL && iconURL ? ' logo-media' : ''}">
      <div class="project-placeholder ${escapeHTML(type)}"${main.src ? ' hidden' : ''}>${fallback}</div>
      ${main.src ? `<img src="${escapeHTML(main.src)}"${imageAttributes(main)} alt="${title} ${screenshotURL ? 'screenshot' : 'project logo'}" width="560" height="350" loading="lazy" decoding="async" referrerpolicy="no-referrer">` : ''}
      ${logo.src ? `<img class="project-logo" src="${escapeHTML(logo.src)}"${imageAttributes(logo)} alt="${title} logo" width="58" height="58" loading="lazy" decoding="async" referrerpolicy="no-referrer">` : ''}
      ${addressIcon}
    </div>`;
  }

  function selectedProjectPayload(project, type) {
    return {
      ...project,
      title: cleanCatalogText(project.title || project.name || 'Minecraft project') || 'Minecraft project',
      type,
      project_type: type,
      summary: cleanCatalogText(project.summary || project.description || ''),
      gallery: asArray(project.gallery).slice(0, 16),
      source_links: asArray(project.source_links).slice(0, 8),
      categories: asArray(project.categories).slice(0, 24)
    };
  }

  function cardHTML(project) {
    project = normalizeProject(project);
    if (isPaperMCDownloadListing(project)) return '';
    const type = normalizeType(project.project_type || project.type);
    const source = sourceKey(project);
    const title = cleanCatalogText(project.title || project.name || 'Minecraft project') || 'Minecraft project';
    const author = cleanCatalogText(project.author || project.display_author || project.user || project.username || '');
    const route = projectRoute(project, type);
    const sourceId = String(project.source_id || project.cf_id || project.modrinth_id || project.id || project.slug || project.modrinth_slug || '');
    const catalogKey = String(project.catalog_key || (sourceId ? `${source}:${sourceId}` : ''));
    const projectKey = `${source}:${String(project.cf_id || project.modrinth_id || project.modrinth_slug || project.id || project.slug || project.url || title).toLowerCase()}`;
    const liveKey = `${source}:${type}:${String(project.cf_id || project.modrinth_id || project.modrinth_slug || project.id || project.slug || title).toLowerCase()}`;
    const tierClass = '';
    const downloads = project.downloads ? formatCount(project.downloads) : '';
    const versions = versionLabel(project);
    const loaders = loaderLabel(project);
    const updated = dateLabel(project.updated || project.updated_at || project.date_modified || project.dateModified);
    const sourceCount = Array.isArray(project.source_links) ? project.source_links.length : 0;
    const sourceBadges = `<span class="source-safety-badge">${escapeHTML(sourceLabel(project))}</span><span class="source-safety-badge">Not mirrored</span>${sourceCount > 1 ? `<span class="source-safety-badge multi-source-badge">Available on ${sourceCount} sources</span>` : ''}`;
    const compatibility = `${versions ? `<span data-live-field="versions">Versions: ${escapeHTML(versions)}</span>` : ''}${loaders ? `<span>&bull;</span><span data-live-field="loaders">Loaders: ${escapeHTML(loaders)}</span>` : ''}${updated ? `<span>&bull;</span><span data-live-field="updated">Updated ${escapeHTML(updated)}</span>` : ''}`;

    const mediaHTML = cardMedia(project, type);
    if (!mediaHTML) return '';
    const previewClass = type === 'skin' ? ' preview-pending' : '';
    const previewAttribute = type === 'skin' ? ' data-requires-preview="true"' : '';
    selectedProjects.set(projectKey, selectedProjectPayload(project, type));
    return `<a class="project-card reveal${tierClass}${previewClass}"${previewAttribute} href="${escapeHTML(route)}" data-live-project-card data-project-open-key="${escapeHTML(projectKey)}" data-live-key="${escapeHTML(liveKey)}" data-live-catalog-key="${escapeHTML(catalogKey)}" data-live-source="${escapeHTML(source)}" data-live-source-id="${escapeHTML(sourceId)}" data-live-type="${escapeHTML(type)}" data-live-title="${escapeHTML(title)}" data-live-author="${escapeHTML(author)}" data-live-slug="${escapeHTML(project.modrinth_slug || project.slug || '')}" data-live-modrinth-id="${escapeHTML(project.modrinth_id || '')}" data-live-cf-id="${escapeHTML(project.cf_id || '')}" data-live-cf-slug="${escapeHTML(project.cf_slug || '')}" data-live-cf-url="${escapeHTML(project.cf_url || project.curseforge_url || '')}" data-live-url="${escapeHTML(project.url || project.external_url || '')}">${mediaHTML}<div class="meta-row source-row">${sourceBadges}<span class="project-sync-status" data-live-field="sync" aria-live="polite"></span></div><div class="meta-row"><span>${escapeHTML(niceType(type))}</span><span>&bull;</span><span>${escapeHTML(project.platform || project.source || 'Official source')}</span>${sourceCount > 1 ? `<span>&bull;</span><span>${sourceCount} sources</span>` : ''}${downloads ? `<span>&bull;</span><span data-live-field="downloads">${escapeHTML(downloads)}</span>` : ''}</div><h3 data-live-field="title">${escapeHTML(title)}</h3>${author ? `<div class="meta-row"><span>by ${escapeHTML(author)}</span></div>` : ''}${compatibility ? `<div class="meta-row compatibility-row">${compatibility}</div>` : ''}<p data-live-field="summary">${escapeHTML(cleanCatalogText(project.summary || project.description || 'Open the official listing for details, versions, media, and creator links.'))}</p><div class="meta-row">${categoryPills(project)}</div></a>`;
  }

  function setupProjectHandoff() {
    document.addEventListener('click', event => {
      const card = event.target.closest?.('a.project-card[data-project-open-key]');
      if (!card) return;
      const project = selectedProjects.get(card.dataset.projectOpenKey);
      if (!project) return;
      try {
        const target = new URL(card.href, location.origin);
        sessionStorage.setItem('fmmSelectedProjectV148', JSON.stringify({
          ts: Date.now(), target: target.pathname + target.search, project
        }));
      } catch { /* Storage is an optional speed path. */ }
    }, true);
  }

  function setupNavigation() {
    const nav = document.querySelector('[data-nav]');
    const toggle = document.querySelector('[data-nav-toggle]');
    if (!nav || !toggle || nav.dataset.navigationReady === 'true') return;

    nav.dataset.navigationReady = 'true';
    nav.id ||= 'siteNavigation';
    toggle.setAttribute('aria-controls', nav.id);
    toggle.setAttribute('aria-expanded', 'false');

    const mediaQuery = matchMedia(MOBILE_NAV_QUERY);
    const isDrawerMode = () => mediaQuery.matches;
    const originalToggleText = toggle.textContent || 'Menu';
    let closeTimer = 0;
    let previouslyFocused = null;

    let backdrop = document.querySelector('[data-nav-backdrop]');
    if (!backdrop) {
      backdrop = document.createElement('button');
      backdrop.type = 'button';
      backdrop.className = 'nav-drawer-backdrop';
      backdrop.dataset.navBackdrop = '';
      backdrop.setAttribute('aria-label', 'Close navigation');
      backdrop.setAttribute('tabindex', '-1');
      document.body.append(backdrop);
    }

    const groups = [...nav.querySelectorAll('[data-nav-menu]')];
    const focusableSelector = [
      'a[href]:not([hidden])',
      'button:not([disabled]):not([hidden])',
      '[tabindex]:not([tabindex="-1"]):not([hidden])'
    ].join(',');

    function closeGroups(except = null) {
      for (const group of groups) {
        if (group === except) continue;
        group.classList.remove('open');
        group.querySelector('.nav-menu-button')?.setAttribute('aria-expanded', 'false');
      }
    }

    function setClosedAccessibility() {
      if (isDrawerMode() && !nav.classList.contains('open')) {
        nav.setAttribute('aria-hidden', 'true');
        nav.inert = true;
      } else {
        nav.setAttribute('aria-hidden', 'false');
        nav.inert = false;
      }
    }

    function setDrawer(open, { restoreFocus = true } = {}) {
      const drawerOpen = Boolean(open && isDrawerMode());
      if (drawerOpen) {
        previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : toggle;
        closeGroups();
        nav.scrollTop = 0;
      }

      nav.classList.toggle('open', drawerOpen);
      document.body.classList.toggle('nav-open', drawerOpen);
      backdrop.classList.toggle('open', drawerOpen);
      toggle.classList.toggle('is-open', drawerOpen);
      toggle.setAttribute('aria-expanded', String(drawerOpen));
      toggle.setAttribute('aria-label', drawerOpen ? 'Close navigation' : 'Open navigation');
      toggle.textContent = drawerOpen ? 'X' : originalToggleText;
      setClosedAccessibility();

      if (drawerOpen) {
        requestAnimationFrame(() => {
          const firstControl = nav.querySelector('.nav-menu-button, .nav-direct, a[href]');
          firstControl?.focus({ preventScroll: true });
        });
      } else {
        closeGroups();
        if (restoreFocus && previouslyFocused?.isConnected) {
          requestAnimationFrame(() => previouslyFocused.focus({ preventScroll: true }));
        }
        previouslyFocused = null;
      }
    }

    function openGroup(group, focusFirst = false) {
      clearTimeout(closeTimer);
      closeGroups(group);
      group.classList.add('open');
      group.querySelector('.nav-menu-button')?.setAttribute('aria-expanded', 'true');
      if (focusFirst) {
        requestAnimationFrame(() => group.querySelector('.nav-menu-panel a')?.focus({ preventScroll: true }));
      }
    }

    function closeGroup(group) {
      group.classList.remove('open');
      group.querySelector('.nav-menu-button')?.setAttribute('aria-expanded', 'false');
    }

    function scheduleClose(group) {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        if (!group.matches(':hover') && !group.contains(document.activeElement)) closeGroup(group);
      }, 220);
    }

    function trapDrawerFocus(event) {
      if (!isDrawerMode() || !nav.classList.contains('open') || event.key !== 'Tab') return;
      const controls = [...nav.querySelectorAll(focusableSelector)].filter(element => {
        if (!(element instanceof HTMLElement) || element.offsetParent === null) return false;
        const panel = element.closest('.nav-menu-panel');
        return !panel || panel.closest('[data-nav-menu]')?.classList.contains('open');
      });
      controls.unshift(toggle);
      if (!controls.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    toggle.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      setDrawer(!nav.classList.contains('open'));
    });
    backdrop.addEventListener('click', () => setDrawer(false));

    for (const group of groups) {
      const button = group.querySelector('.nav-menu-button');
      const links = [...group.querySelectorAll('.nav-menu-panel a')];
      if (!button) continue;

      button.addEventListener('click', event => {
        event.preventDefault();
        if (group.classList.contains('open')) closeGroup(group);
        else openGroup(group);
      });

      group.addEventListener('pointerenter', () => {
        if (!isDrawerMode() && matchMedia('(hover: hover)').matches) openGroup(group);
      });
      group.addEventListener('pointerleave', () => {
        if (!isDrawerMode() && matchMedia('(hover: hover)').matches) scheduleClose(group);
      });
      group.addEventListener('focusin', () => {
        if (!isDrawerMode()) openGroup(group);
      });
      group.addEventListener('focusout', () => {
        if (!isDrawerMode()) scheduleClose(group);
      });

      button.addEventListener('keydown', event => {
        if (['ArrowDown', 'Enter', ' '].includes(event.key)) {
          event.preventDefault();
          openGroup(group, true);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          closeGroup(group);
          button.focus({ preventScroll: true });
        }
      });

      links.forEach((link, index) => link.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          links[(index + 1) % links.length]?.focus({ preventScroll: true });
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          links[(index - 1 + links.length) % links.length]?.focus({ preventScroll: true });
        } else if (event.key === 'Escape') {
          event.preventDefault();
          closeGroup(group);
          button.focus({ preventScroll: true });
        }
      }));
    }

    nav.addEventListener('click', event => {
      if (event.target.closest('a')) setDrawer(false, { restoreFocus: false });
    });

    document.addEventListener('pointerdown', event => {
      if (!isDrawerMode()) {
        if (!nav.contains(event.target) && !toggle.contains(event.target)) closeGroups();
        return;
      }
      if (nav.classList.contains('open') && !nav.contains(event.target) && !toggle.contains(event.target)) {
        setDrawer(false);
      }
    }, true);

    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        if (nav.classList.contains('open')) {
          event.preventDefault();
          setDrawer(false);
        } else closeGroups();
      } else trapDrawerFocus(event);
    });

    const syncNavigationMode = () => {
      if (!isDrawerMode()) {
        setDrawer(false, { restoreFocus: false });
        nav.removeAttribute('aria-hidden');
        nav.inert = false;
      } else {
        closeGroups();
        setClosedAccessibility();
      }
    };

    mediaQuery.addEventListener?.('change', syncNavigationMode);
    window.addEventListener('orientationchange', () => setDrawer(false, { restoreFocus: false }), { passive: true });
    window.addEventListener('pageshow', () => setDrawer(false, { restoreFocus: false }), { passive: true });

    const currentPath = location.pathname.replace(/\/$/, '') || '/';
    nav.querySelectorAll('a[href]').forEach(link => {
      const linkPath = new URL(link.href, location.origin).pathname.replace(/\/$/, '') || '/';
      const active = linkPath === currentPath;
      link.classList.toggle('active', active);
      if (active) {
        link.setAttribute('aria-current', 'page');
        link.closest('[data-nav-menu]')?.querySelector('.nav-menu-button')?.classList.add('active');
      }
    });

    syncNavigationMode();
  }

  function setupAccountGates() {
    const gatedPaths = new Set(['/claim', '/dashboard', '/collaboration', '/modpack-builder']);
    document.addEventListener('click', async event => {
      const link = event.target.closest?.('a[data-requires-account]');
      if (!link) return;
      const target = new URL(link.href, location.origin);
      if (!gatedPaths.has(target.pathname)) return;
      event.preventDefault();
      try {
        const response = await fetch('/api/auth/me', { headers: { accept: 'application/json' } });
        const data = response.ok ? await response.json() : { user: null };
        location.href = data.user ? target.href : `/account?next=${encodeURIComponent(target.pathname + target.search)}`;
      } catch {
        location.href = `/account?next=${encodeURIComponent(target.pathname + target.search)}`;
      }
    }, true);
  }

  const effectSections = new Set();
  let scrollResumeTimer = 0;
  function registerProjectEffects(root = document) {
    const candidates = [
      ...(root.matches?.('#libraryGrid') ? [root] : []),
      ...root.querySelectorAll?.('#libraryGrid') || []
    ].filter(candidate => candidate.querySelector('.project-card'));
    for (const section of candidates) {
      if (effectSections.has(section)) continue;
      effectSections.add(section);
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
          for (const entry of entries) entry.target.classList.toggle('fmm-effects-active', entry.isIntersecting);
        }, { rootMargin: '220px 0px', threshold: .01 });
        observer.observe(section);
      } else section.classList.add('fmm-effects-active');
    }
  }

  function setupProjectEffectPause() {
    window.addEventListener('scroll', () => {
      for (const section of effectSections) {
        if (section.classList.contains('fmm-effects-active')) section.classList.add('fmm-effects-paused');
      }
      clearTimeout(scrollResumeTimer);
      scrollResumeTimer = setTimeout(() => {
        for (const section of effectSections) section.classList.remove('fmm-effects-paused');
      }, 150);
    }, { passive: true });
    document.addEventListener('visibilitychange', () => {
      for (const section of effectSections) section.classList.toggle('fmm-effects-paused', document.hidden);
    });
  }

  const cardSyncTimes = new Map();
  let cardSyncTimer = 0;
  let cardSyncInFlight = false;
  let cardSyncPending = false;
  let cardSyncObserver = null;
  const CARD_SYNC_TTL = 10 * 60 * 1000;

  function replaceSyncedCard(card, data) {
    if (!data || data.error || !data.card_synced) return card;
    const merged = normalizeProject(data);
    const html = cardHTML(merged);
    if (!html) return card;
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    const replacement = template.content.firstElementChild;
    if (!(replacement instanceof HTMLElement)) return card;
    if (card.classList.contains('is-visible')) replacement.classList.add('is-visible');
    replacement.dataset.cardSyncedAt = data.fetchedAt || new Date().toISOString();
    const syncField = replacement.querySelector('[data-live-field="sync"]');
    if (syncField) syncField.textContent = `${data.source === 'curseforge' ? 'CurseForge' : 'Modrinth'} - card synced just now`;
    card.replaceWith(replacement);
    registerProjectEffects(replacement.parentElement || document);
    return replacement;
  }

  function cardSyncItem(card) {
    const source = card.dataset.liveSource || '';
    const id = card.dataset.liveSourceId || card.dataset.liveCfId || card.dataset.liveModrinthId || card.dataset.liveSlug || '';
    if (!['curseforge', 'modrinth'].includes(source) || !id) return null;
    return {
      clientKey: card.dataset.liveKey,
      catalog_key: card.dataset.liveCatalogKey || '',
      source,
      source_id: id,
      id,
      type: card.dataset.liveType || '',
      project_type: card.dataset.liveType || '',
      title: card.dataset.liveTitle || '',
      author: card.dataset.liveAuthor || '',
      slug: card.dataset.liveSlug || '',
      cf_id: card.dataset.liveCfId || '',
      cf_slug: card.dataset.liveCfSlug || '',
      modrinth_id: card.dataset.liveModrinthId || ''
    };
  }

  async function refreshVisibleLiveStats(root = document) {
    const scopeCards = root?.matches?.('[data-live-project-card]')
      ? [root]
      : [...(root?.querySelectorAll?.('[data-live-project-card]') || [])];
    const candidates = scopeCards.length ? scopeCards : [...document.querySelectorAll('[data-live-project-card]')];
    const now = Date.now();
    const cards = candidates
      .filter(card => card.isConnected && card.getBoundingClientRect().top < innerHeight * 2.4)
      .filter(card => {
        const key = card.dataset.liveKey || '';
        return key && now - Number(cardSyncTimes.get(key) || 0) >= CARD_SYNC_TTL;
      })
      .slice(0, 36);
    if (!cards.length) return;

    const items = [];
    const cardByKey = new Map();
    for (const card of cards) {
      const item = cardSyncItem(card);
      if (!item || !item.clientKey || cardByKey.has(item.clientKey)) continue;
      items.push(item);
      cardByKey.set(item.clientKey, card);
    }
    if (!items.length) return;

    if (cardSyncInFlight) {
      cardSyncPending = true;
      return;
    }
    cardSyncInFlight = true;
    try {
      const response = await fetch('/api/catalog/cards', {
        method: 'POST',
        cache: 'no-store',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!response.ok) return;
      const payload = await response.json();
      const results = payload.items || {};
      for (const item of items) {
        const card = cardByKey.get(item.clientKey);
        const data = results[item.clientKey];
        if (!card || !data || data.error) continue;
        cardSyncTimes.set(item.clientKey, Date.now());
        replaceSyncedCard(card, data);
      }
    } catch (error) {
      if (window.FMM_DEBUG) console.warn('Project-card synchronization deferred', error);
    } finally {
      cardSyncInFlight = false;
      if (cardSyncPending) {
        cardSyncPending = false;
        queueCardSync(document, 160);
      }
    }
  }

  function observeCardSync(root = document) {
    if (!('IntersectionObserver' in window)) return;
    if (!cardSyncObserver) {
      cardSyncObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) queueCardSync(document, 120);
      }, { rootMargin: '1200px 0px', threshold: 0.01 });
    }
    const cards = root?.matches?.('[data-live-project-card]')
      ? [root]
      : [...(root?.querySelectorAll?.('[data-live-project-card]') || [])];
    for (const card of cards) cardSyncObserver.observe(card);
  }

  function queueCardSync(root = document, delay = 180) {
    clearTimeout(cardSyncTimer);
    cardSyncTimer = setTimeout(() => refreshVisibleLiveStats(root), delay);
  }

  function scheduleOneLiveRefresh() {
    let started = false;
    const run = () => {
      if (started) return;
      started = true;
      queueCardSync(document, 0);
    };
    for (const eventName of ['pointerdown', 'keydown']) {
      window.addEventListener(eventName, run, { once: true, passive: true, capture: true });
    }
    const afterLoad = () => {
      if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 8000 });
      else setTimeout(run, 8000);
    };
    if (document.readyState === 'complete') afterLoad();
    else window.addEventListener('load', afterLoad, { once: true, passive: true });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') queueCardSync(document, 300);
    });
  }

  function setupImageRecovery() {
    document.addEventListener('error', event => {
      const image = event.target;
      if (!(image instanceof HTMLImageElement)) return;
      const fallbackURL = image.dataset.fallbackSrc || '';
      if (fallbackURL && image.dataset.fmmImageRetry !== 'fallback') {
        image.dataset.fmmImageRetry = 'fallback';
        image.src = fallbackURL;
        return;
      }
      if (image.dataset.requiredPreview === 'true') {
        const card = image.closest('.project-card[data-requires-preview="true"]');
        const key = card?.dataset.liveKey || '';
        card?.remove();
        document.dispatchEvent(new CustomEvent('fmm:required-preview-failed', { detail: { key } }));
        return;
      }
      image.hidden = true;
      const media = image.closest('.project-media');
      media?.querySelector('.project-placeholder, .creator-project-logo-fallback')?.removeAttribute('hidden');
      media?.classList.add('image-failed');
    }, true);

    document.addEventListener('load', event => {
      const image = event.target;
      if (!(image instanceof HTMLImageElement) || !image.closest('.project-media')) return;
      if (image.dataset.requiredPreview === 'true') {
        const card = image.closest('.project-card[data-requires-preview="true"]');
        card?.classList.remove('preview-pending');
        card?.setAttribute('data-preview-ready', 'true');
      }
      if (image.matches('.creator-project-logo-image')) {
        image.closest('.project-media')?.classList.add('has-project-logo');
      }
    }, true);
  }

  function setupBackToTop() {
    const button = document.createElement('button');
    button.className = 'back-to-top';
    button.type = 'button';
    button.setAttribute('aria-label', 'Back to top');
    button.innerHTML = '<span aria-hidden="true">&#8593;</span>';
    document.body.append(button);
    let ticking = false;
    const update = () => {
      ticking = false;
      button.classList.toggle('show', scrollY > 850);
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    button.addEventListener('click', () => scrollTo({ top: 0, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }));
  }

  window.esc = escapeHTML;
  window.fmt = formatCount;
  window.niceType = niceType;
  window.cardHTML = cardHTML;
  window.refreshReveals = root => root?.querySelectorAll?.('.reveal:not(.is-visible)').forEach(item => item.classList.add('is-visible'));
  window.registerProjectEffects = registerProjectEffects;
  window.refreshLiveProjectStats = (root = document) => {
    observeCardSync(root);
    queueCardSync(root, 120);
  };

  setupNavigation();
  setupAccountGates();
  setupProjectHandoff();
  setupImageRecovery();
  setupProjectEffectPause();
  registerProjectEffects(document);
  observeCardSync(document);
  scheduleOneLiveRefresh();
  setupBackToTop();
})();
